import { useState } from 'react';
import { AlertDialog as AlertDialogPrimitive } from 'radix-ui';
import { buttonVariants } from './button.jsx';
import { cn } from '../lib/utils.js';
import { LoaderCircle } from 'lucide-react';

/**
 * Confirmation for (destructive) actions. `onConfirm` may return a promise; the dialog
 * stays open and shows a spinner until it resolves.
 * @param {{
 *   open?: boolean,
 *   onOpenChange?: (open: boolean) => void,
 *   trigger?: import('react').ReactNode,
 *   title: import('react').ReactNode,
 *   description?: import('react').ReactNode,
 *   affectedCount?: number,
 *   confirmLabel?: string,
 *   cancelLabel?: string,
 *   destructive?: boolean,
 *   onConfirm: () => unknown | Promise<unknown>,
 * }} props
 */
export function ConfirmDialog({
  open,
  onOpenChange,
  trigger,
  title,
  description,
  affectedCount,
  confirmLabel = 'Bestätigen',
  cancelLabel = 'Abbrechen',
  destructive = false,
  onConfirm,
}) {
  const [internalOpen, setInternalOpen] = useState(false);
  const [pending, setPending] = useState(false);
  const isOpen = open ?? internalOpen;
  const setOpen = (value) => {
    if (open === undefined) setInternalOpen(value);
    onOpenChange?.(value);
  };

  async function handleConfirm(event) {
    event.preventDefault();
    setPending(true);
    try {
      await onConfirm();
      setOpen(false);
    } finally {
      setPending(false);
    }
  }

  return (
    <AlertDialogPrimitive.Root open={isOpen} onOpenChange={(value) => !pending && setOpen(value)}>
      {trigger && <AlertDialogPrimitive.Trigger asChild>{trigger}</AlertDialogPrimitive.Trigger>}
      <AlertDialogPrimitive.Portal>
        <AlertDialogPrimitive.Overlay data-slot="confirm-dialog-overlay" className="fixed inset-0 z-50 bg-black/40" />
        <AlertDialogPrimitive.Content
          data-slot="confirm-dialog-content"
          className="fixed left-1/2 top-1/2 z-50 grid w-[calc(100%-2rem)] max-w-md -translate-x-1/2 -translate-y-1/2 gap-4 rounded-lg border bg-popover p-5 text-popover-foreground shadow-sm"
        >
          <div className="flex flex-col gap-1.5">
            <AlertDialogPrimitive.Title className="text-h3 font-semibold">{title}</AlertDialogPrimitive.Title>
            {description ? (
              <AlertDialogPrimitive.Description className="text-sm text-muted-foreground">{description}</AlertDialogPrimitive.Description>
            ) : (
              <AlertDialogPrimitive.Description className="sr-only">{typeof title === 'string' ? title : 'Bestätigung'}</AlertDialogPrimitive.Description>
            )}
            {typeof affectedCount === 'number' && (
              <p className="text-sm">
                Betroffene Datensätze: <strong className="numeric">{affectedCount.toLocaleString('de-DE')}</strong>
              </p>
            )}
          </div>
          <div data-slot="confirm-dialog-footer" className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <AlertDialogPrimitive.Cancel className={buttonVariants({ variant: 'outline' })} disabled={pending}>
              {cancelLabel}
            </AlertDialogPrimitive.Cancel>
            <AlertDialogPrimitive.Action
              className={cn(buttonVariants({ variant: destructive ? 'destructive' : 'default' }))}
              onClick={handleConfirm}
              disabled={pending}
            >
              {pending && <LoaderCircle className="animate-spin" aria-hidden="true" />}
              {confirmLabel}
            </AlertDialogPrimitive.Action>
          </div>
        </AlertDialogPrimitive.Content>
      </AlertDialogPrimitive.Portal>
    </AlertDialogPrimitive.Root>
  );
}
